import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Me, Manager, Lang, Period, Screen, MonthStatus } from "../lib/types";
import { getMe, getManagers, patchLang as apiPatchLang } from "../lib/api";
import { t as tFn } from "../lib/i18n";
import { useAvailableMonths } from "../lib/useAvailableMonths";

interface AuthState {
  me: Me | null;
  error: string | null;
  maintenanceMsg: { ru: string; uk: string };
  managers: Manager[];
  managerId: string;
  period: Period;
  periodId: string;
  screen: Screen;
  setManagerId: (id: string) => void;
  setPeriod: (p: Period) => void;
  setPeriodId: (id: string) => void;
  setScreen: (s: Screen) => void;
  setLang: (l: Lang) => void;
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  showAdmin: boolean;
  setShowAdmin: (v: boolean) => void;
  adminInitialTab: string | null;
  setAdminInitialTab: (tab: string | null) => void;
  setMaintenanceMode: (on: boolean) => void;
  availableMonths: string[];
  monthStatuses: MonthStatus[];
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceMsg, setMaintenanceMsg] = useState<{ ru: string; uk: string }>({ ru: "", uk: "" });
  const [managers, setManagers] = useState<Manager[]>([]);
  const [managerId, setManagerId] = useState<string>("");
  const [period, setPeriod] = useState<Period>("month");
  const [periodId, setPeriodId] = useState<string>("");
  const [screen, setScreen] = useState<Screen>("stats");
  const [lang, setLangState] = useState<Lang>("ru");
  const [showAdmin, setShowAdmin] = useState<boolean>(false);
  const [adminInitialTab, setAdminInitialTab] = useState<string | null>(null);
  const { months: availableMonths, statuses: monthStatuses } = useAvailableMonths();

  useEffect(() => {
    getMe()
      .then(async (m) => {
        setMe(m);
        setLangState(m.lang);
        if (m.is_admin) {
          setManagerId("__dept__");
          try {
            const { managers } = await getManagers();
            setManagers(managers);
          } catch { /* non-fatal */ }
        } else if (m.manager_id) {
          setManagerId(m.manager_id);
        }
      })
      .catch((e: any) => {
        if (e.message === "MAINTENANCE") {
          setError("MAINTENANCE");
          setMaintenanceMsg({
            ru: e.maintenance?.message_ru ?? "",
            uk: e.maintenance?.message_uk ?? "",
          });
          return;
        }
        setError(e.message);
      });
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    apiPatchLang(l).catch(() => { /* non-fatal */ });
  };

  function setMaintenanceMode(on: boolean) {
    setMe(prev => prev ? { ...prev, maintenance_mode: on } : prev);
  }

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") {
        getMe().then(setMe).catch(() => { /* swallow */ });
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const value: AuthState = useMemo(() => ({
    me, error, maintenanceMsg, managers,
    managerId, period, periodId, screen,
    setManagerId, setPeriod, setPeriodId, setScreen, setLang,
    lang,
    t: (key, vars) => tFn(key, lang, vars),
    showAdmin, setShowAdmin,
    adminInitialTab, setAdminInitialTab,
    setMaintenanceMode,
    availableMonths, monthStatuses,
  }), [me, error, maintenanceMsg, managers, managerId, period, periodId, screen, lang, showAdmin, adminInitialTab, availableMonths, monthStatuses]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

export function useT() {
  const { t } = useAuth();
  return t;
}
